import { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Shell } from "@/components/Shell";
import { OverviewTab } from "@/components/coord/OverviewTab";
import { JobsTab } from "@/components/coord/JobsTab";
import { WorkersTab } from "@/components/coord/WorkersTab";
import { ClientsTab } from "@/components/coord/ClientsTab";
import { RosterTab } from "@/components/coord/RosterTab";
import { TimesheetsTab } from "@/components/coord/TimesheetsTab";
import { TimeOffTab } from "@/components/coord/TimeOffTab";
import { ReportsTab } from "@/components/coord/ReportsTab";
import { PresetsTab } from "@/components/coord/PresetsTab";
import { DepotsTab } from "@/components/coord/DepotsTab";
import { InvoicesTab } from "@/components/coord/InvoicesTab";
import { HoursTab } from "@/components/coord/HoursTab";
import { WelcomeSetup } from "@/components/WelcomeSetup";
import { BillingDialog } from "@/components/billing/BillingDialog";
import { LockedScreen } from "@/components/billing/LockedScreen";
import { useDepot } from "@/context/DepotContext";
import { useCompany } from "@/context/CompanyContext";
import { hasModule } from "@/lib/modules";
import { LayoutDashboard, Briefcase, Users, Building2, CalendarRange, Clock, CalendarOff, BarChart3, SlidersHorizontal, MapPinned, Receipt, Hourglass } from "lucide-react";

const TABS = [
  { v: "overview", label: "Overview", icon: LayoutDashboard, C: OverviewTab, need: "jobs" },
  { v: "jobs", label: "Jobs", icon: Briefcase, C: JobsTab, need: "jobs" },
  { v: "workers", label: "Crew", icon: Users, C: WorkersTab },
  { v: "clients", label: "Clients", icon: Building2, C: ClientsTab, need: "clients" },
  { v: "invoices", label: "Invoices", icon: Receipt, C: InvoicesTab, need: "invoices" },
  { v: "roster", label: "Roster", icon: CalendarRange, C: RosterTab },
  { v: "timesheets", label: "Timesheets", icon: Clock, C: TimesheetsTab },
  { v: "hours", label: "Hours", icon: Hourglass, C: HoursTab },
  { v: "reports", label: "Reports", icon: BarChart3, C: ReportsTab, need: "jobs" },
  { v: "presets", label: "Presets", icon: SlidersHorizontal, C: PresetsTab, need: "jobs" },
  { v: "timeoff", label: "Time Off", icon: CalendarOff, C: TimeOffTab },
  { v: "depots", label: "Depots", icon: MapPinned, C: DepotsTab, admin: true },
];

export default function CoordinatorDashboard() {
  const { isAdmin, version } = useDepot();
  const { company } = useCompany();
  const tabs = TABS.filter((t) => (!t.admin || isAdmin) && hasModule(company, t.need));
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const initialTab = TABS.some((t) => t.v === params.get("tab")) ? params.get("tab") : "overview";
  const [tab, setTab] = useState(initialTab);
  const current = tabs.some((t) => t.v === tab) ? tab : (tabs.some((t) => t.v === "overview") ? "overview" : "timesheets");
  const [focusJobId, setFocusJobId] = useState(params.get("job") || null);
  const [createSignal, setCreateSignal] = useState(0);
  const [invoiceClient, setInvoiceClient] = useState(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.search) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const goCreateJob = () => { setTab("jobs"); setCreateSignal((n) => n + 1); };
  if (company?.billing?.locked) return <LockedScreen />;

  return (
    <Tabs value={current} onValueChange={(v) => { setTab(v); setInvoiceClient(null); }} className="w-full">
      <Shell
        title="Coordinator"
        tabs={
          <TabsList className="h-auto bg-transparent p-0 gap-1 overflow-x-auto flex justify-start w-full rounded-none">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger
                  key={t.v}
                  value={t.v}
                  data-testid={`tab-${t.v}`}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none data-[state=active]:bg-transparent px-3 py-3 text-sm font-medium text-muted-foreground shrink-0"
                >
                  <Icon className="w-4 h-4 mr-1.5" /> {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        }
      >
        {tabs.map((t) => {
          const C = t.C;
          let props = {};
          if (t.v === "jobs") props = { focusJobId, createSignal, onFocusHandled: () => setFocusJobId(null) };
          if (t.v === "overview") props = { onNewJob: goCreateJob, onOpenJob: (id) => { setFocusJobId(id); setTab("jobs"); } };
          if (t.v === "clients") props = hasModule(company, "invoices") ? { onShowInvoices: (c) => { setInvoiceClient(c); setTab("invoices"); } } : {};
          if (t.v === "invoices") props = { clientFilter: invoiceClient, onClearClient: () => setInvoiceClient(null) };
          return <TabsContent key={t.v} value={t.v} className="mt-0"><C key={version} {...props} /></TabsContent>;
        })}
      </Shell>
      <WelcomeSetup />
      <BillingDialog />
    </Tabs>
  );
}
